import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import enhancedAuthService from '../services/enhanced-auth-service';

// API base URL from environment
const API_BASE_URL = 'https://a3d1kmonz2.execute-api.us-east-1.amazonaws.com/dev' || "";

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
});

// List of endpoints that are public and don't require authentication
const PUBLIC_ENDPOINTS = [
  '/outfits',
  '/search',
  '/trending',
  '/public',
  '/users/', // Allow anonymous access to user profiles
];

// Check if URL is a public endpoint
const isPublicEndpoint = (url: string): boolean => {
  return PUBLIC_ENDPOINTS.some(endpoint => url.includes(endpoint));
};

// Add request interceptor for authentication
api.interceptors.request.use(
  async (config) => {
    try {
      const url = config.url || '';
      const isPublic = isPublicEndpoint(url);
      
      console.log('🔍 apiUtils: Processing request:', {
        method: config.method?.toUpperCase(),
        url,
        isPublic
      });
      
      // Always try to get token if user is authenticated
      const isAuthenticated = await enhancedAuthService.isAuthenticated();
      
      if (isAuthenticated) {
        const token = await enhancedAuthService.getAccessToken();
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`;
          console.log('✅ apiUtils: Added auth header for authenticated user');
        }
      } else if (!isPublic) {
        console.log('⚠️ apiUtils: No auth for protected endpoint - will likely get 401');
      } else {
        console.log('📖 apiUtils: Public endpoint, no auth needed');
      }
    } catch (error) {
      console.warn('⚠️ apiUtils: Error handling auth for request:', error);
      // Continue without token - let the backend handle authentication
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error: AxiosError) => {
    const url = error.config?.url || '';
    const isPublic = isPublicEndpoint(url);
    
    // Handle 401 errors gracefully without retry loops
    if (error.response?.status === 401) {
      if (isPublic) {
        console.log('📖 401 on public endpoint (unexpected):', url);
      } else {
        console.log('🔒 401 on protected endpoint (expected without auth):', url);
      }
      // Don't attempt automatic retry or logout - let the component handle it
      // This prevents infinite loops and allows proper error handling
    }

    return Promise.reject(error);
  }
);

/**
 * Make an API request with retry mechanism
 * @param config Axios request config
 * @param retries Number of retries
 * @param retryDelay Delay between retries in ms
 * @returns Promise with response
 */
export const apiRequest = async <T>(
  config: AxiosRequestConfig,
  retries = 3,
  retryDelay = 1000
): Promise<AxiosResponse<T>> => {
  try {
    return await api(config);
  } catch (error) {
    if (retries <= 0) {
      throw error;
    }

    // Only retry on network errors or 5xx server errors
    const shouldRetry =
      !axios.isAxiosError(error) ||
      !error.response ||
      error.response.status >= 500;

    if (!shouldRetry) {
      throw error;
    }

    // Wait before retrying
    await new Promise((resolve) => setTimeout(resolve, retryDelay));

    // Retry with exponential backoff
    return apiRequest<T>(config, retries - 1, retryDelay * 2);
  }
};

/**
 * API utility functions with retry mechanism
 */
export const apiUtils = {
  /**
   * Make a GET request
   * @param url URL to request
   * @param config Axios request config
   * @returns Promise with response data
   */
  get: async <T>(url: string, config?: AxiosRequestConfig): Promise<T> => {
    const response = await apiRequest<T>({
      ...config,
      method: "GET",
      url,
    });
    return response.data;
  },

  /**
   * Make a POST request
   * @param url URL to request
   * @param data Data to send
   * @param config Axios request config
   * @returns Promise with response data
   */
  post: async <T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<T> => {
    const response = await apiRequest<T>({
      ...config,
      method: "POST",
      url,
      data,
    });
    return response.data;
  },

  /**
   * Make a PUT request
   * @param url URL to request
   * @param data Data to send
   * @param config Axios request config
   * @returns Promise with response data
   */
  put: async <T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<T> => {
    const response = await apiRequest<T>({
      ...config,
      method: "PUT",
      url,
      data,
    });
    return response.data;
  },

  /**
   * Make a DELETE request
   * @param url URL to request
   * @param config Axios request config
   * @returns Promise with response data
   */
  delete: async <T>(url: string, config?: AxiosRequestConfig): Promise<T> => {
    const response = await apiRequest<T>({
      ...config,
      method: "DELETE",
      url,
    });
    return response.data;
  },
};

/**
 * Legacy apiCall function for backward compatibility
 * @deprecated Use apiUtils methods instead
 */
export const apiCall = async <T>(
  url: string,
  options: {
    method?: string;
    data?: any;
    headers?: Record<string, string>;
  } = {}
): Promise<T> => {
  const { method = 'GET', data, headers } = options;
  
  const config: AxiosRequestConfig = {
    method: method as any,
    url,
    data,
    headers,
  };
  
  const response = await apiRequest<T>(config);
  return response.data;
};

export default apiUtils;
